import React, { useState } from 'react';
import {  paymentMethodConfigurationForPayment, paymentMethodConfigurationForSaveMethod, paymentMethodConfigurationForVerification, PK } from '../constant/cons';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from './CheckoutForm';
import { loadStripe } from '@stripe/stripe-js';
import SubscriptionPayment from './SubscriptionPayment'; // Import the new subscription component
import BankDetails from './BankDetails';
import { bankDetails } from '../constant/bank';
import { siteConfig } from '../constant/siteConfig';
import { currencyConfig } from '../constant/currency';
import { useLanguage } from '../i18n/LanguageContext';
import { initializePayment } from '../services/stripeApi';

const stripePromise = loadStripe(PK);

const Home = () => {
    const { t } = useLanguage();

    const [formData, setFormData] = useState({
        email: '',
        amount: '0.00',
    });
    const [load, setLoad] = useState(false);
    const [clientSecret, setClientSecret] = useState('');
    const [paymentType, setPaymentType] = useState('');
    const [currency, setCurrency] = useState(currencyConfig.defaultCurrency);

    const paymentTypeMenu = [
        { key: 'manual', labelKey: 'home.paymentTypes.manual' },
        { key: 'bank-transfer', labelKey: 'home.paymentTypes.bankTransfer' },
        { key: 'subscription', labelKey: 'home.paymentTypes.subscription' },
        { key: 'authorization', labelKey: 'home.paymentTypes.authorization' },
        { key: 'verification', labelKey: 'home.paymentTypes.verification' },
    ];

    const selectedPaymentTypeKey =
        paymentTypeMenu.find((menu) => menu.key === paymentType)?.labelKey;

    const selectedPaymentTypeLabel = selectedPaymentTypeKey ? t(selectedPaymentTypeKey) : '';

    const resetToMainMenu = () => {
        setLoad(false);
        setClientSecret('');
        setPaymentType('');
        setCurrency(currencyConfig.defaultCurrency);
        setFormData({
            email: '',
            amount: '0.00',
        });
    };

    const handleSelectPaymentType = (type) => {
        setClientSecret('');
        setPaymentType(type);
    };

    const options = {
        clientSecret,
        appearance: {
            theme: 'stripe',
        },
    };

    const normalizeEmail = (value = '') => value.trim().toLowerCase();

    // Payment creation for non-subscription types
    const handlePaymentCreation = async () => {
        setLoad(true);
        try {
            const normalizedEmail = normalizeEmail(formData.email);
            if (!normalizedEmail) {
                alert(t('home.alerts.invalidEmail'));
                setLoad(false);
                return;
            }
            if (paymentType === 'subscription') {
                // Subscription is now handled in the SubscriptionPayment component.
                setLoad(false);
                return;
            }
            if (normalizedEmail !== formData.email) {
                setFormData((prev) => ({ ...prev, email: normalizedEmail }));
            }

            let response;

            switch (paymentType) {
                case 'authorization': {
                    response = await initializePayment({
                        paymentType: 'authorization',
                        email: normalizedEmail,
                        paymentMethodConfigurationId: paymentMethodConfigurationForSaveMethod,
                    });
                    break;
                }
                case 'verification': {
                    response = await initializePayment({
                        paymentType: 'verification',
                        email: normalizedEmail,
                        currency,
                        paymentMethodConfigurationId: paymentMethodConfigurationForVerification,
                    });
                    break;
                }
                case 'manual': {
                    response = await initializePayment({
                        paymentType: 'manual',
                        email: normalizedEmail,
                        amount: formData.amount.replace('$', ''),
                        currency,
                        paymentMethodConfigurationId: paymentMethodConfigurationForPayment,
                    });
                    break;
                }
                default:
                    alert(t('home.alerts.invalidPaymentType'));
                    setLoad(false);
                    return;
            }

            setClientSecret(response.clientSecret);
            setLoad(false);
        } catch (error) {
            console.error('Payment error:', error);
            alert(error.response?.data?.message || t('home.alerts.initError'));
            setLoad(false);
        }
    };

    const getButtonLabel = () => {
        switch (paymentType) {
            case "authorization":
                return t('home.actionButton.authorization');
            case "verification":
                return t('home.actionButton.verification');
            case "manual":
                return t('home.actionButton.manual');
            case "bank-transfer":
                return t('home.paymentTypes.bankTransfer');
            case "subscription":
                return t('home.actionButton.subscription');
            default:
                return t('home.actionButton.default');
        }
    };

    return (
        <div className="container min-h-screen h-full py-6 max-w-4xl mx-auto p-4">
            <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                    {/* <h2 className="card-title mb-4">Payment Portal</h2> */}
                    <div className="space-y-4">
                        {!paymentType ? (
                            <div className="space-y-3">
                                <p className="text-sm font-medium text-gray-600">{t('home.selectPaymentAction')}</p>
                                <div className="flex flex-col gap-3">
                                    {paymentTypeMenu.map((menu) => (
                                        <button
                                            key={menu.key}
                                            className="btn h-auto min-h-[52px] justify-start hover:opacity-90"
                                            style={{
                                                backgroundColor: siteConfig.brandColors.primary,
                                                color: siteConfig.brandColors.buttonText,
                                            }}
                                            onClick={() => handleSelectPaymentType(menu.key)}
                                        >
                                            {t(menu.labelKey)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <button
                                        className="btn btn-ghost btn-sm h-auto min-h-0 w-fit px-0 hover:bg-transparent hover:underline"
                                        style={{ color: siteConfig.brandColors.primary }}
                                        onClick={resetToMainMenu}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M19 12H5" />
                                            <path d="M12 19l-7-7 7-7" />
                                        </svg>
                                        {t('common.goBack')}
                                    </button>
                                    <h3 className="text-lg font-semibold">{selectedPaymentTypeLabel}</h3>
                                </div>

                                {/* Render non-subscription payment fields */}
                                {paymentType !== 'subscription' && (
                                    <>
                                        {
                                            paymentType === 'verification' ? <>
                                                <p className='border rounded-lg p-2'>{t('home.verificationNote')}</p>
                                            </>:<> </>
                                        }

                                        {
                                            paymentType === 'bank-transfer' ?
                                            <>
                                                    <BankDetails banksData={bankDetails}></BankDetails>
                                            </>
                                            :
                                            <>
                                                    {
                                                        paymentType === 'authorization' ?
                                                        <></>
                                                        :
                                                        <>
                                                                <div className="form-control">
                                                                    <label className="label">
                                                                        <span className="label-text">{t('common.currency')}</span>
                                                                    </label>
                                                                    <select
                                                                        className="select select-bordered"
                                                                        value={currency}
                                                                        onChange={(e) => setCurrency(e.target.value)}
                                                                        disabled={!!clientSecret}
                                                                    >
                                                                        {currencyConfig.supportedCurrencies.map((item) => (
                                                                            <option key={item.code} value={item.code}>
                                                                                {item.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                        </>
                                                    }

                                                    <div className="form-control">
                                                        <label className="label">
                                                            <span className="label-text">{t('common.email')}</span>
                                                        </label>
                                                        <input
                                                            type="email"
                                                            className="input input-bordered"
                                                            value={formData.email}
                                                            onChange={(e) =>
                                                                setFormData({
                                                                    ...formData,
                                                                    email: normalizeEmail(e.target.value),
                                                                })
                                                            }
                                                            disabled={!!clientSecret}
                                                            required
                                                        />
                                                    </div>
                                             </>
                                        }

                                        {paymentType === 'manual' && (
                                            <div className="form-control">
                                                <label className="label">
                                                    <span className="label-text">
                                                        {t('common.amountWithCurrency', { currency: currency.toUpperCase() })}
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="input input-bordered"
                                                    value={formData.amount.replace('$', '')}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            amount: `$${e.target.value}`,
                                                        })
                                                    }
                                                    min="0"
                                                    disabled={!!clientSecret}
                                                    step="0.01"
                                                    required
                                                />
                                            </div>
                                        )}

                                        {!clientSecret ? (
                                            <>
                                                {
                                                    paymentType === 'bank-transfer' ?
                                                    <></>
                                                    :
                                                        <button
                                                            className="btn w-full hover:opacity-90"
                                                            style={{
                                                                backgroundColor: siteConfig.brandColors.primary,
                                                                color: siteConfig.brandColors.buttonText,
                                                            }}
                                                            onClick={handlePaymentCreation}
                                                            disabled={load || !paymentType}
                                                        >
                                                            {load ? (
                                                                <span className="loading loading-spinner"></span>
                                                            ) : (
                                                                getButtonLabel()
                                                            )}
                                                        </button>
                                                }
                                            </>
                                        ) : (
                                            <Elements stripe={stripePromise} options={options}>
                                                <CheckoutForm
                                                    paymentType={paymentType}
                                                    email={normalizeEmail(formData.email)}
                                                    currency={currency}
                                                />
                                            </Elements>
                                        )}
                                    </>
                                )}

                                {/* Render the SubscriptionPayment component if payment type is subscription */}
                                {paymentType === 'subscription' && <SubscriptionPayment  />}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
